/**
 * Filtrado Avanzado de Productos por Combinaciones Color-Talle
 * 
 * Este script resuelve el problema de filtrado cuando se usan filtros separados
 * de color y talle. Consulta las variantes disponibles de cada producto para
 * verificar que exista la combinación exacta seleccionada (ej: "Blanco-S").
 * 
 * Funcionamiento:
 * 1. Detecta los filtros activos de color y/o talle
 * 2. Para cada producto visible, consulta sus variantes disponibles usando el endpoint JSON de Shopify
 * 3. Verifica si existe una combinación exacta de color-talle con stock disponible
 * 4. Oculta los productos que no tengan la combinación exacta seleccionada
 */

class AdvancedProductFilter {
  constructor() {
    // Cache de productos consultados para evitar peticiones repetidas
    this.productCache = new Map();
    
    // Cache de conteos de filtros por stock disponible
    this.filterCountsCache = new Map();
    
    // Namespace y keys de los metacampos (aunque no los usamos directamente,
    // los mantenemos por referencia)
    this.namespace = 'custom';
    this.colorKey = 'color';
    this.talleKey = 'talles';
    
    // Flag para evitar actualizaciones durante la interacción del usuario
    this.isUserInteracting = false;
    this.updateTimeout = null;
    
    // Inicializar el filtrado
    this.init();
  }

  /**
   * Inicializa el sistema de filtrado
   * Configura los listeners y observadores para detectar cambios en el grid de productos
   */
  init() {
    // Detectar cuando el usuario está interactuando con los filtros
    this.setupFilterInteractionListeners();
    
    // Ejecutar filtrado inicial después de un breve delay para asegurar que el DOM esté listo
    setTimeout(() => {
      this.updateFilterCounts();
      this.filterProducts();
    }, 300);
    
    // Escuchar el evento 'paginate' que se dispara cuando se actualiza el grid de productos
    document.addEventListener('paginate', () => {
      if (!this.isUserInteracting) {
        this.scheduleUpdate();
      }
    });
    
    // Observar cambios en el DOM del grid de productos
    // Esto permite detectar cuando Search & Discovery actualiza los productos
    const gridContainer = document.getElementById('CollectionProductGrid');
    if (gridContainer) {
      const observer = new MutationObserver(() => {
        // Solo actualizar si el usuario no está interactuando
        if (!this.isUserInteracting) {
          this.scheduleUpdate();
        }
      });
      
      // Observar cambios en los hijos del grid (cuando se agregan/eliminan productos)
      observer.observe(gridContainer, { childList: true, subtree: true });
    }
  }

  /**
   * Configura listeners para detectar cuando el usuario interactúa con los filtros
   */
  setupFilterInteractionListeners() {
    // Detectar cuando se hace clic en un filtro (summary)
    document.addEventListener('click', (e) => {
      const filterSummary = e.target.closest('.facets__summary');
      if (filterSummary) {
        this.isUserInteracting = true;
        // Limpiar el timeout de actualización si existe
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
          this.updateTimeout = null;
        }
        // Marcar que el usuario dejó de interactuar después de un tiempo
        setTimeout(() => {
          this.isUserInteracting = false;
        }, 2000);
      }
    }, true);

    // Detectar cuando se hace clic en un checkbox de filtro
    document.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox' && e.target.closest('.facets__disclosure')) {
        this.isUserInteracting = true;
        // Limpiar el timeout de actualización si existe
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
          this.updateTimeout = null;
        }
        // Marcar que el usuario dejó de interactuar después de un tiempo
        setTimeout(() => {
          this.isUserInteracting = false;
        }, 2000);
      }
    }, true);
  }

  /**
   * Programa una actualización con debounce para evitar ejecuciones excesivas
   */
  scheduleUpdate() {
    // Limpiar timeout anterior si existe
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // Programar nueva actualización
    this.updateTimeout = setTimeout(() => {
      if (!this.isUserInteracting) {
        this.updateFilterCounts();
        this.filterProducts();
      }
    }, 500);
  }

  /**
   * Obtiene los filtros activos desde la URL y los elementos del DOM
   * 
   * Busca filtros de color y talle activos en dos lugares:
   * 1. Los botones de filtros activos en el DOM (.active-facets__button)
   * 2. Los parámetros de la URL (para compatibilidad con Search & Discovery)
   * 
   * @returns {Object} Objeto con arrays de colores y talles filtrados
   */
  getActiveFilters() {
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = {
      colors: [],
      talles: []
    };

    // Buscar filtros activos en los botones del DOM
    // Estos son los "chips" que muestran los filtros seleccionados
    document.querySelectorAll('.active-facets__button:not([href*="clear_all"])').forEach(button => {
      const text = button.textContent.trim().replace(/[×✕]/g, '').trim();
      const href = button.getAttribute('href') || '';
      
      // Los filtros de color tienen el atributo data-swatch
      if (button.hasAttribute('data-swatch')) {
        if (text && !activeFilters.colors.includes(this.normalizeValue(text))) {
          activeFilters.colors.push(this.normalizeValue(text));
        }
      } else if (text) {
        // Para detectar filtros de talle, revisamos la URL del botón
        // Los filtros de Search & Discovery incluyen el nombre del filtro en la URL
        const lowerHref = href.toLowerCase();
        const lowerText = text.toLowerCase();
        
        // Si la URL contiene indicadores de talle/tamaño, es un filtro de talle
        if (lowerHref.includes('talle') || 
            lowerHref.includes('talles') ||
            lowerHref.includes('tamaño') || 
            lowerHref.includes('size') || 
            lowerHref.includes('talla')) {
          if (!activeFilters.talles.includes(this.normalizeValue(text))) {
            activeFilters.talles.push(this.normalizeValue(text));
          }
        }
        // Si no es color y no parece ser talle, podría ser otro tipo de filtro
        // En ese caso no lo agregamos a nuestros filtros
      }
    });

    // También buscar en los parámetros de la URL
    // Esto es útil para filtros de Search & Discovery que usan parámetros como:
    // filter.p.metafields.custom.color o filter.v.option.color
    urlParams.forEach((value, key) => {
      if (key.includes('filter.p.metafields') || key.includes('filter.v.option')) {
        const filterValue = decodeURIComponent(value);
        const lowerKey = key.toLowerCase();
        
        // Detectar si es un filtro de color
        if (lowerKey.includes(this.colorKey) || 
            lowerKey.includes('color') || 
            lowerKey.includes('colour')) {
          activeFilters.colors.push(this.normalizeValue(filterValue));
        } 
        // Detectar si es un filtro de talle/tamaño
        else if (lowerKey.includes(this.talleKey) || 
                 lowerKey.includes('size') ||
                 lowerKey.includes('tamaño') ||
                 lowerKey.includes('talle') || 
                 lowerKey.includes('talla')) {
          activeFilters.talles.push(this.normalizeValue(filterValue));
        }
      }
    });

    // Eliminar duplicados usando Set
    activeFilters.colors = [...new Set(activeFilters.colors)];
    activeFilters.talles = [...new Set(activeFilters.talles)];

    return activeFilters;
  }

  /**
   * Normaliza un valor para comparación
   * Convierte a minúsculas y elimina espacios en los extremos
   * 
   * @param {string} value - Valor a normalizar
   * @returns {string} Valor normalizado
   */
  normalizeValue(value) {
    return value.toString().toLowerCase().trim();
  }

  /**
   * Obtiene el handle del producto desde los data-attributes o la URL
   * 
   * El handle es necesario para consultar las variantes del producto
   * usando el endpoint JSON de Shopify
   * 
   * @param {HTMLElement} productCard - Elemento del producto en el DOM
   * @returns {string|null} Handle del producto o null si no se encuentra
   */
  getProductHandle(productCard) {
    // Primero intentar obtener desde el atributo data-product-handle
    // (esto lo agregamos en el archivo Liquid)
    const handle = productCard.getAttribute('data-product-handle');
    if (handle) return handle;

    // Si no está disponible, intentar extraer desde la URL del producto
    const url = productCard.getAttribute('data-product-url') || 
                productCard.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    const match = url.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  /**
   * Consulta las variantes disponibles de un producto usando el endpoint JSON de Shopify
   * 
   * El endpoint /products/{handle}.js devuelve información completa del producto
   * incluyendo todas sus variantes y su disponibilidad.
   * 
   * @param {string} handle - Handle del producto (ej: "remera-basica")
   * @returns {Promise<Object|null>} Objeto con las combinaciones disponibles o null si hay error
   */
  async fetchProductVariants(handle) {
    // Verificar si ya tenemos este producto en el cache
    // Esto evita hacer peticiones HTTP repetidas para el mismo producto
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }

    try {
      // Consultar el endpoint JSON de Shopify
      // Este endpoint es público y no requiere autenticación
      const response = await fetch(`/products/${handle}.js`);
      
      if (!response.ok) {
        console.warn(`No se pudo obtener variantes para el producto: ${handle}`);
        return null;
      }

      const product = await response.json();
      
      // Encontrar los índices de las opciones de Color y Talle
      // Esto es necesario porque los productos pueden tener las opciones en diferente orden
      const colorOptionIndex = product.options.findIndex(opt => 
        ['color', 'colour', 'couleur', 'colore', 'farbe', 'färg', 'farve'].includes(opt.name.toLowerCase())
      );
      
      const talleOptionIndex = product.options.findIndex(opt => 
        ['size', 'talla', 'talle', 'taille', 'tamaño', 'tamanho', 'größe'].includes(opt.name.toLowerCase())
      );

      // Si encontramos ambas opciones, extraer las combinaciones disponibles
      const availableCombinations = [];
      const availableVariants = []; // Guardar también las variantes disponibles
      
      if (colorOptionIndex !== -1 && talleOptionIndex !== -1) {
        // Iterar sobre todas las variantes del producto
        product.variants.forEach(variant => {
          // Solo considerar variantes que están disponibles (tienen stock)
          if (variant.available) {
            const color = variant.options[colorOptionIndex];
            const talle = variant.options[talleOptionIndex];
            
            // Si la variante tiene ambos valores, crear la combinación
            if (color && talle) {
              // Crear combinación en formato "color-talle" (ej: "blanco-s")
              const combination = `${this.normalizeValue(color)}-${this.normalizeValue(talle)}`;
              availableCombinations.push(combination);
              
              // Guardar la variante con su información
              availableVariants.push({
                variant: variant,
                color: color,
                talle: talle,
                combination: combination
              });
            }
          }
        });
      }

      // Preparar el resultado con las combinaciones únicas y las variantes
      const result = {
        combinations: [...new Set(availableCombinations)], // Eliminar duplicados
        variants: availableVariants, // Variantes disponibles con su información
        product, // Guardar también el producto completo por si se necesita después
        colorOptionIndex,
        talleOptionIndex
      };

      // Guardar en cache para futuras consultas
      this.productCache.set(handle, result);
      
      return result;
      
    } catch (error) {
      console.error(`Error al obtener variantes para el producto ${handle}:`, error);
      return null;
    }
  }

  /**
   * Encuentra la variante que debe mostrarse según los filtros activos
   * 
   * @param {Object} productData - Datos del producto con variantes disponibles
   * @param {Object} activeFilters - Objeto con los filtros activos (colors, talles)
   * @returns {Object|null} Variante que debe mostrarse o null si no se encuentra
   */
  findVariantToShow(productData, activeFilters) {
    if (!productData || !productData.variants || productData.variants.length === 0) {
      return null;
    }

    // CASO 1: Solo hay filtro de COLOR
    // Encontrar la primera variante disponible con ese color
    if (activeFilters.colors.length > 0 && activeFilters.talles.length === 0) {
      for (const color of activeFilters.colors) {
        const normalizedColor = this.normalizeValue(color);
        const matchingVariant = productData.variants.find(v => 
          this.normalizeValue(v.color) === normalizedColor
        );
        if (matchingVariant) {
          return matchingVariant.variant;
        }
      }
    }

    // CASO 2: Solo hay filtro de TALLE
    // Encontrar la primera variante disponible con ese talle
    if (activeFilters.talles.length > 0 && activeFilters.colors.length === 0) {
      for (const talle of activeFilters.talles) {
        const normalizedTalle = this.normalizeValue(talle);
        const matchingVariant = productData.variants.find(v => 
          this.normalizeValue(v.talle) === normalizedTalle
        );
        if (matchingVariant) {
          return matchingVariant.variant;
        }
      }
    }

    // CASO 3: Hay AMBOS filtros (COLOR y TALLE)
    // Encontrar la variante con la combinación exacta
    if (activeFilters.colors.length > 0 && activeFilters.talles.length > 0) {
      for (const color of activeFilters.colors) {
        for (const talle of activeFilters.talles) {
          const combo = `${this.normalizeValue(color)}-${this.normalizeValue(talle)}`;
          const matchingVariant = productData.variants.find(v => 
            v.combination === combo
          );
          if (matchingVariant) {
            return matchingVariant.variant;
          }
        }
      }
    }

    return null;
  }

  /**
   * Verifica si un producto debe mostrarse basándose en los filtros activos
   * 
   * Esta es la función principal de lógica de filtrado:
   * - Si no hay filtros activos: mostrar todos los productos
   * - Si solo hay filtro de color: mostrar productos que tengan ese color disponible
   * - Si solo hay filtro de talle: mostrar productos que tengan ese talle disponible
   * - Si hay ambos filtros: mostrar solo productos que tengan la combinación EXACTA
   * 
   * @param {HTMLElement} productCard - Elemento del producto en el DOM
   * @param {Object} activeFilters - Objeto con los filtros activos (colors, talles)
   * @returns {Promise<Object>} Objeto con {shouldShow: boolean, variant: Object|null}
   */
  async shouldShowProduct(productCard, activeFilters) {
    // Si no hay filtros activos, mostrar todos los productos
    if (activeFilters.colors.length === 0 && activeFilters.talles.length === 0) {
      return { shouldShow: true, variant: null };
    }

    // Obtener el handle del producto
    const handle = this.getProductHandle(productCard);
    if (!handle) {
      // Si no podemos obtener el handle, mostrar el producto (mejor mostrar de más que de menos)
      return { shouldShow: true, variant: null };
    }

    // Consultar las variantes disponibles del producto
    const productData = await this.fetchProductVariants(handle);
    
    // Si no podemos obtener los datos del producto, mostrarlo por defecto
    if (!productData || productData.combinations.length === 0) {
      return { shouldShow: true, variant: null };
    }

    const combinations = productData.combinations;

    // CASO 1: Solo hay filtro de COLOR
    // Mostrar productos que tengan al menos una combinación con ese color
    if (activeFilters.colors.length > 0 && activeFilters.talles.length === 0) {
      const hasMatchingColor = activeFilters.colors.some(color => 
        combinations.some(combo => combo.startsWith(this.normalizeValue(color) + '-'))
      );
      if (hasMatchingColor) {
        const variant = this.findVariantToShow(productData, activeFilters);
        return { shouldShow: true, variant };
      }
      return { shouldShow: false, variant: null };
    }

    // CASO 2: Solo hay filtro de TALLE
    // Mostrar productos que tengan al menos una combinación con ese talle
    if (activeFilters.talles.length > 0 && activeFilters.colors.length === 0) {
      const hasMatchingTalle = activeFilters.talles.some(talle => 
        combinations.some(combo => combo.endsWith('-' + this.normalizeValue(talle)))
      );
      if (hasMatchingTalle) {
        const variant = this.findVariantToShow(productData, activeFilters);
        return { shouldShow: true, variant };
      }
      return { shouldShow: false, variant: null };
    }

    // CASO 3: Hay AMBOS filtros (COLOR y TALLE)
    // Aquí está la lógica clave: solo mostrar productos que tengan la COMBINACIÓN EXACTA
    // Por ejemplo, si filtramos "Blanco" y "S", solo mostrar productos que tengan "blanco-s" disponible
    if (activeFilters.colors.length > 0 && activeFilters.talles.length > 0) {
      const hasExactMatch = activeFilters.colors.some(color => {
        return activeFilters.talles.some(talle => {
          // Crear la combinación exacta que estamos buscando
          const combo = `${this.normalizeValue(color)}-${this.normalizeValue(talle)}`;
          // Verificar si esta combinación existe en las combinaciones disponibles del producto
          return combinations.includes(combo);
        });
      });
      if (hasExactMatch) {
        const variant = this.findVariantToShow(productData, activeFilters);
        return { shouldShow: true, variant };
      }
      return { shouldShow: false, variant: null };
    }

    // Por defecto, mostrar el producto
    return { shouldShow: true, variant: null };
  }

  /**
   * Actualiza la tarjeta del producto para mostrar la variante correcta
   * 
   * @param {HTMLElement} productCard - Elemento product-card del producto
   * @param {Object} variant - Variante que debe mostrarse
   */
  updateProductCardVariant(productCard, variant) {
    if (!productCard || !variant) return;

    // Buscar el swatch de color que corresponde a esta variante
    // Buscar por data-variant-id primero (más específico)
    let matchingSwatch = productCard.querySelector(`[data-swatch-variant-id="${variant.id}"]`);
    
    // Si no encontramos por data-swatch-variant-id, buscar por data-variant
    if (!matchingSwatch) {
      const swatches = productCard.querySelectorAll('[data-variant]');
      for (const swatch of swatches) {
        try {
          const swatchVariant = JSON.parse(swatch.getAttribute('data-variant'));
          if (swatchVariant && swatchVariant.id === variant.id) {
            matchingSwatch = swatch;
            break;
          }
        } catch (e) {
          // Ignorar errores de parsing
        }
      }
    }

    // Si encontramos el swatch, simular un clic para actualizar la tarjeta
    if (matchingSwatch) {
      // Disparar el evento de clic para actualizar imagen, precio y enlaces
      // Usar un pequeño delay para asegurar que el componente esté listo
      setTimeout(() => {
        matchingSwatch.click();
      }, 50);
    } else {
      // Si no hay swatch, actualizar manualmente la imagen y el precio
      this.updateCardManually(productCard, variant);
    }
  }

  /**
   * Actualiza manualmente la tarjeta del producto cuando no hay swatch disponible
   * 
   * @param {HTMLElement} productCard - Elemento product-card del producto
   * @param {Object} variant - Variante que debe mostrarse
   */
  updateCardManually(productCard, variant) {
    if (!productCard || !variant) return;

    // Actualizar la imagen si la variante tiene una imagen
    if (variant.featured_image) {
      const productCardImage = productCard.querySelector('[data-product-card-image]');
      const productCardSwatchImage = productCard.querySelector('[data-product-card-swatch-image]');
      
      if (productCardImage && productCardSwatchImage) {
        const imgSrc = variant.featured_image.src;
        const imgAlt = variant.featured_image.alt || '';
        
        productCardSwatchImage.setAttribute('src', imgSrc);
        productCardSwatchImage.setAttribute('alt', imgAlt);
        productCardSwatchImage.style.opacity = '1';
        productCardImage.style.opacity = '0';
      }
    }

    // Actualizar los enlaces con la variante
    const cardLinks = productCard.querySelectorAll('[data-product-card-link]');
    const variantUrl = variant.url || productCard.getAttribute('data-product-url');
    
    cardLinks.forEach(link => {
      if (variantUrl) {
        link.setAttribute('href', variantUrl);
      }
    });

    // Intentar actualizar el precio usando el método del componente product-card
    // Si el componente está disponible, usar su método updateCardPrice
    if (productCard.updateCardPrice && typeof productCard.updateCardPrice === 'function') {
      // Buscar un elemento que tenga los datos de precio de la variante
      const priceElement = productCard.querySelector(`[data-variant-id="${variant.id}"]`);
      if (priceElement) {
        productCard.updateCardPrice(priceElement);
      }
    }
  }

  /**
   * Filtra los productos en el grid basándose en los filtros activos
   * 
   * Esta función es el corazón del sistema:
   * 1. Obtiene los filtros activos
   * 2. Itera sobre todos los productos visibles
   * 3. Para cada producto, verifica si debe mostrarse
   * 4. Oculta los productos que no cumplen con los criterios
   * 5. Actualiza la variante mostrada según los filtros
   */
  async filterProducts() {
    // Desactivado: con filtros de Search & Discovery (p. ej. Tamaño/King) esta lógica
    // elegía variante en la tarjeta y podía mostrar AGOTADO o precio incorrectos frente a Liquid.
    return;

    // Obtener el contenedor del grid de productos
    const productGrid = document.querySelector('#template--collection');
    if (!productGrid) {
      // Si no existe el grid, no hay nada que hacer
      return;
    }

    // Obtener los filtros activos
    const activeFilters = this.getActiveFilters();
    
    // Si no hay filtros activos, mostrar todos los productos
    if (activeFilters.colors.length === 0 && activeFilters.talles.length === 0) {
      const hiddenItems = productGrid.querySelectorAll('.grid__item[style*="display: none"]');
      hiddenItems.forEach(item => item.style.display = '');
      this.hideNoResultsMessage();
      return;
    }

    // Obtener todos los productos en el grid
    const productCards = Array.from(productGrid.querySelectorAll('product-card.grid-view-item, .grid-view-item product-card'));
    
    if (productCards.length === 0) {
      return;
    }

    // Mostrar indicador visual de que se está procesando
    // Reducir opacidad mientras se procesan los productos
    productCards.forEach(card => {
      const gridItem = card.closest('.grid__item') || card.parentElement;
      if (gridItem) {
        gridItem.style.opacity = '0.5';
      }
    });

    // Verificar cada producto de forma asíncrona
    // Usamos Promise.all para procesar todos los productos en paralelo
    const promises = productCards.map(async (card) => {
      const gridItem = card.closest('.grid__item') || card.parentElement;
      if (!gridItem) return;

      // Verificar si este producto debe mostrarse y obtener la variante correcta
      const result = await this.shouldShowProduct(card, activeFilters);
      
      if (result.shouldShow) {
        // Mostrar el producto
        gridItem.style.display = '';
        gridItem.style.opacity = '1';
        
        // Si hay una variante específica que debe mostrarse, actualizar la tarjeta
        if (result.variant) {
          // Usar requestAnimationFrame y setTimeout para asegurar que el DOM esté listo
          // y que el componente product-card esté completamente inicializado
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.updateProductCardVariant(card, result.variant);
            }, 150);
          });
        }
      } else {
        // Ocultar el producto
        gridItem.style.display = 'none';
      }
    });

    // Esperar a que todas las verificaciones terminen
    await Promise.all(promises);

    // Verificar si quedan productos visibles
    const visibleItems = Array.from(productGrid.querySelectorAll('.grid__item')).filter(item => 
      item.style.display !== 'none' && window.getComputedStyle(item).display !== 'none'
    );

    // Si no hay productos visibles, mostrar un mensaje
    if (visibleItems.length === 0) {
      this.showNoResultsMessage();
    } else {
      this.hideNoResultsMessage();
    }
  }

  /**
   * Muestra un mensaje cuando no hay productos que coincidan con los filtros
   */
  showNoResultsMessage() {
    let messageEl = document.querySelector('.advanced-filter-no-results');
    
    if (!messageEl) {
      const grid = document.querySelector('#template--collection');
      if (grid && grid.parentElement) {
        // Crear el elemento del mensaje
        messageEl = document.createElement('div');
        messageEl.className = 'advanced-filter-no-results';
        messageEl.style.cssText = 'grid-column: 1/-1; text-align: center; padding: 2rem; color: var(--text-color, #000);';
        messageEl.textContent = 'No se encontraron productos con la combinación seleccionada.';
        
        // Insertar después del grid
        grid.parentElement.insertBefore(messageEl, grid.nextSibling);
      }
    }
  }

  /**
   * Oculta el mensaje de "sin resultados"
   */
  hideNoResultsMessage() {
    const messageEl = document.querySelector('.advanced-filter-no-results');
    if (messageEl) {
      messageEl.remove();
    }
  }

  /**
   * Obtiene todos los productos de la colección actual
   * 
   * @returns {Array<string>} Array de handles de productos
   */
  getAllProductHandles() {
    const productGrid = document.querySelector('#template--collection');
    if (!productGrid) return [];

    const productCards = Array.from(productGrid.querySelectorAll('product-card.grid-view-item, .grid-view-item product-card'));
    const handles = [];

    productCards.forEach(card => {
      const handle = this.getProductHandle(card);
      if (handle) {
        handles.push(handle);
      }
    });

    return [...new Set(handles)]; // Eliminar duplicados
  }

  /**
   * Calcula los conteos de filtros basándose en el stock disponible
   * 
   * @returns {Promise<Object>} Objeto con conteos por color y talle
   */
  async calculateFilterCounts() {
    const handles = this.getAllProductHandles();
    if (handles.length === 0) return { colors: {}, talles: {} };

    const colorCounts = {};
    const talleCounts = {};

    // Procesar productos en paralelo pero con límite para no sobrecargar
    const BATCH_SIZE = 10;
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      const batch = handles.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (handle) => {
        const productData = await this.fetchProductVariants(handle);
        if (!productData || !productData.variants) return;

        // Contar colores disponibles
        const availableColors = new Set();
        const availableTalles = new Set();

        productData.variants.forEach(v => {
          if (v.color) {
            const normalizedColor = this.normalizeValue(v.color);
            availableColors.add(normalizedColor);
          }
          if (v.talle) {
            const normalizedTalle = this.normalizeValue(v.talle);
            availableTalles.add(normalizedTalle);
          }
        });

        // Incrementar conteos
        availableColors.forEach(color => {
          colorCounts[color] = (colorCounts[color] || 0) + 1;
        });

        availableTalles.forEach(talle => {
          talleCounts[talle] = (talleCounts[talle] || 0) + 1;
        });
      });

      await Promise.all(promises);
    }

    return { colors: colorCounts, talles: talleCounts };
  }

  /**
   * Actualiza los conteos y disponibilidad de las opciones de filtro
   */
  async updateFilterCounts() {
    // Desactivado: recalculaba conteos solo con la rejilla visible y ocultaba .facets__item
    // (display:none) cuando el conteo local era 0, desalineado de Search & Discovery y Liquid.
    // Eso hacía desaparecer opciones como King/Tamaño. filterProducts() sigue activo para la rejilla.
    return;

    // No actualizar si el usuario está interactuando
    if (this.isUserInteracting) {
      return;
    }

    // Esperar un poco para asegurar que el DOM esté completamente cargado
    // y que el sistema de filtrado de Shopify se haya inicializado
    await new Promise(resolve => setTimeout(resolve, 800));

    // Verificar si hay filtros en el DOM
    const filters = document.querySelectorAll('.facets__disclosure');
    if (filters.length === 0) {
      // Si no hay filtros aún, intentar de nuevo más tarde
      setTimeout(() => this.updateFilterCounts(), 1000);
      return;
    }

    // Guardar el estado de qué filtros están abiertos
    const openFilters = new Set();
    filters.forEach(filter => {
      if (filter.hasAttribute('open')) {
        openFilters.add(filter);
      }
    });

    // Calcular conteos basados en stock disponible
    const counts = await this.calculateFilterCounts();

    // Actualizar filtros de color
    this.updateColorFilters(counts.colors);

    // Actualizar filtros de talle/tamaño
    this.updateTalleFilters(counts.talles);

    // Restaurar el estado abierto de los filtros
    openFilters.forEach(filter => {
      if (filter && filter.parentElement) {
        filter.setAttribute('open', '');
      }
    });
  }

  /**
   * Actualiza los filtros de color con los conteos correctos
   * 
   * @param {Object} colorCounts - Objeto con conteos por color
   */
  updateColorFilters(colorCounts) {
    const colorFilters = document.querySelectorAll('.facets__disclosure');
    
    colorFilters.forEach(filter => {
      const filterLabel = filter.querySelector('.facets__summary span')?.textContent?.trim().toLowerCase();
      if (!filterLabel) return;

      // Verificar si es un filtro de color
      const colorLangs = ['color', 'colour', 'couleur', 'colore', 'farbe', 'färg', 'farve'];
      const isColorFilter = colorLangs.some(lang => filterLabel.includes(lang));
      
      if (!isColorFilter) return;

      // Guardar si este filtro está abierto
      const wasOpen = filter.hasAttribute('open');

      const filterValues = filter.querySelectorAll('.facets__item');
      
      filterValues.forEach(item => {
        const label = item.querySelector('label');
        const input = item.querySelector('input[type="checkbox"]');
        
        if (!label || !input) return;

        // Obtener el valor del filtro de diferentes formas posibles
        let filterValue = input.value || input.getAttribute('value') || '';
        
        // Si no hay valor en el input, intentar obtenerlo del label
        if (!filterValue) {
          const labelText = label.textContent || label.innerText || '';
          filterValue = labelText.replace(/\s*\(\d+\)\s*$/, '').trim();
        }
        
        // Normalizar el valor para comparación
        const normalizedValue = this.normalizeValue(filterValue);
        
        // Buscar el conteo real - intentar diferentes variaciones del nombre
        let realCount = 0;
        
        // Buscar coincidencia exacta primero
        if (colorCounts[normalizedValue] !== undefined) {
          realCount = colorCounts[normalizedValue];
        } else {
          // Buscar coincidencias parciales (por si hay diferencias en mayúsculas/espacios)
          for (const [color, count] of Object.entries(colorCounts)) {
            if (this.normalizeValue(color) === normalizedValue || 
                normalizedValue.includes(this.normalizeValue(color)) ||
                this.normalizeValue(color).includes(normalizedValue)) {
              realCount = count;
              break;
            }
          }
        }

        // Actualizar solo el texto del conteo sin tocar los demás elementos del label
        // Buscar el último nodo de texto que contiene el conteo
        const walker = document.createTreeWalker(
          label,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let lastTextNode = null;
        let node;
        while (node = walker.nextNode()) {
          if (node.textContent.match(/\(\d+\)/)) {
            lastTextNode = node;
          }
        }
        
        // Si encontramos un nodo de texto con el conteo, actualizarlo
        if (lastTextNode) {
          const textWithoutCount = lastTextNode.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
          lastTextNode.textContent = ` ${textWithoutCount} (${realCount})`;
        } else {
          // Si no encontramos el nodo, buscar el último nodo de texto y agregar el conteo
          const allTextNodes = [];
          const walker2 = document.createTreeWalker(
            label,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );
          while (node = walker2.nextNode()) {
            allTextNodes.push(node);
          }
          
          if (allTextNodes.length > 0) {
            const lastNode = allTextNodes[allTextNodes.length - 1];
            const currentText = lastNode.textContent.trim();
            const textWithoutCount = currentText.replace(/\s*\(\d+\)\s*$/, '').trim();
            lastNode.textContent = ` ${textWithoutCount} (${realCount})`;
          }
        }

        // Si no hay stock disponible y no está activo, ocultar o deshabilitar
        if (realCount === 0 && !input.checked) {
          label.classList.add('facet-checkbox--disabled');
          input.disabled = true;
          item.style.display = 'none'; // Ocultar completamente
        } else {
          // Asegurar que los elementos con stock estén siempre visibles y habilitados
          label.classList.remove('facet-checkbox--disabled');
          input.disabled = false;
          item.style.display = '';
          item.style.visibility = '';
          item.style.opacity = '';
        }
      });

      // Restaurar el estado abierto si estaba abierto antes
      if (wasOpen) {
        filter.setAttribute('open', '');
      }
    });
  }

  /**
   * Actualiza los filtros de talle/tamaño con los conteos correctos
   * 
   * @param {Object} talleCounts - Objeto con conteos por talle
   */
  updateTalleFilters(talleCounts) {
    const allFilters = document.querySelectorAll('.facets__disclosure');
    
    allFilters.forEach(filter => {
      const filterLabel = filter.querySelector('.facets__summary span')?.textContent?.trim().toLowerCase();
      if (!filterLabel) return;

      // Verificar si es un filtro de talle/tamaño
      const sizeLangs = ['size', 'talla', 'talle', 'taille', 'tamaño', 'tamanho', 'größe'];
      const isSizeFilter = sizeLangs.some(lang => filterLabel.includes(lang));
      
      if (!isSizeFilter) return;

      const filterValues = filter.querySelectorAll('.facets__item');
      
      filterValues.forEach(item => {
        const label = item.querySelector('label');
        const input = item.querySelector('input[type="checkbox"]');
        
        if (!label || !input) return;

        // Obtener el valor del filtro de diferentes formas posibles
        let filterValue = input.value || input.getAttribute('value') || '';
        
        // Si no hay valor en el input, intentar obtenerlo del label
        if (!filterValue) {
          const labelText = label.textContent || label.innerText || '';
          filterValue = labelText.replace(/\s*\(\d+\)\s*$/, '').trim();
        }
        
        // Normalizar el valor para comparación
        const normalizedValue = this.normalizeValue(filterValue);
        
        // Buscar el conteo real - intentar diferentes variaciones del nombre
        let realCount = 0;
        
        // Buscar coincidencia exacta primero
        if (talleCounts[normalizedValue] !== undefined) {
          realCount = talleCounts[normalizedValue];
        } else {
          // Buscar coincidencias parciales (por si hay diferencias en mayúsculas/espacios)
          for (const [talle, count] of Object.entries(talleCounts)) {
            if (this.normalizeValue(talle) === normalizedValue || 
                normalizedValue.includes(this.normalizeValue(talle)) ||
                this.normalizeValue(talle).includes(normalizedValue)) {
              realCount = count;
              break;
            }
          }
        }

        // Actualizar solo el texto del conteo sin tocar los demás elementos del label
        // Buscar el último nodo de texto que contiene el conteo
        const walker = document.createTreeWalker(
          label,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let lastTextNode = null;
        let node;
        while (node = walker.nextNode()) {
          if (node.textContent.match(/\(\d+\)/)) {
            lastTextNode = node;
          }
        }
        
        // Si encontramos un nodo de texto con el conteo, actualizarlo
        if (lastTextNode) {
          const textWithoutCount = lastTextNode.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
          lastTextNode.textContent = ` ${textWithoutCount} (${realCount})`;
        } else {
          // Si no encontramos el nodo, buscar el último nodo de texto y agregar el conteo
          const allTextNodes = [];
          const walker2 = document.createTreeWalker(
            label,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );
          while (node = walker2.nextNode()) {
            allTextNodes.push(node);
          }
          
          if (allTextNodes.length > 0) {
            const lastNode = allTextNodes[allTextNodes.length - 1];
            const currentText = lastNode.textContent.trim();
            const textWithoutCount = currentText.replace(/\s*\(\d+\)\s*$/, '').trim();
            lastNode.textContent = ` ${textWithoutCount} (${realCount})`;
          }
        }

        // Si no hay stock disponible y no está activo, ocultar o deshabilitar
        if (realCount === 0 && !input.checked) {
          label.classList.add('facet-checkbox--disabled');
          input.disabled = true;
          item.style.display = 'none'; // Ocultar completamente
        } else {
          // Asegurar que los elementos con stock estén siempre visibles y habilitados
          label.classList.remove('facet-checkbox--disabled');
          input.disabled = false;
          item.style.display = '';
          item.style.visibility = '';
          item.style.opacity = '';
        }
      });
    });
  }
}

// ============================================================================
// INICIALIZACIÓN
// ============================================================================

// Inicializar el filtrado cuando el DOM esté listo
// Verificamos el estado del documento para decidir cómo inicializar
if (document.readyState === 'loading') {
  // Si el DOM aún se está cargando, esperar al evento DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    new AdvancedProductFilter();
  });
} else {
  // Si el DOM ya está listo, inicializar inmediatamente
  new AdvancedProductFilter();
}
